import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { subjectColor } from "@/lib/constants";
import { QrCode, Sparkles, Keyboard, Save, Upload, Loader2, AlertTriangle, Camera, Aperture, CheckCircle2, HelpCircle, ScanLine, Layers } from "lucide-react";
import { LiveCamera } from "../components/LiveCamera";
import { ReadStatus } from "../components/ReadStatus";
import { beepSuccess, beepWarn, beepError } from "../lib/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Telefon fotoğraflarını küçültür (uzun kenar max px), JPEG döndürür — yükleme boyutu/hızı ve QR çözümü için.
async function downscaleImage(file, maxDim = 3200, quality = 0.95) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = url; });
    let { width, height } = img;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    width = Math.round(width * scale); height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", quality);
  } finally { URL.revokeObjectURL(url); }
}

export default function OpticalReader() {
  const { examId: paramExam } = useParams();
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState(paramExam || "");
  const [exam, setExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [booklet, setBooklet] = useState(""); // Deneme: A/B/C/D
  const [answers, setAnswers] = useState({});
  const [answerKey, setAnswerKey] = useState({}); // doğru/yanlış renklendirmesi için okunan sınav+kitapçık anahtarı
  const [uncertain, setUncertain] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [manualQr, setManualQr] = useState("");
  const [scannedResult, setScannedResult] = useState(null); // QR okutunca anlık sonuç
  const [autoLoading, setAutoLoading] = useState(false);
  const [activeFormId, setActiveFormId] = useState("");
  const [activeStudent, setActiveStudent] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const scannerRef = useRef(null);
  const autoFileRef = useRef(null);

  // Tek kare: tek fotoğraftan QR + işaretli baloncuklar okunur, sonuç önizlenir (onaylayınca kaydedilir)
  const onAutoFrame = async (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    await submitFrame(() => downscaleImage(f), activeFormId);
  };

  const [liveOpen, setLiveOpen] = useState(false);
  const [serial, setSerial] = useState(false);
  const [lastFeedback, setLastFeedback] = useState(null);
  const [readTick, setReadTick] = useState(0);
  const onLiveCapture = async (dataUrl) => {
    const data = await submitFrame(async () => dataUrl, activeFormId);
    if (!data) { beepError(); setLastFeedback({ ok: false, text: "Okunamadı — form 4 köşesiyle tekrar kadraja alın" }); return; }
    const name = `${data.student.first_name} ${data.student.last_name}`;
    const marked = data.marked_count ?? Object.keys(data.answers || {}).length;
    const clean = (data.uncertain || []).length === 0 && marked > 0;
    const isDeneme = data.exam_type === "deneme";
    if (serial && clean && isDeneme && !data.booklet) {
      beepWarn();
      setLastFeedback({ ok: false, text: `${name} · Kitapçık türü okunamadı — seri kayıt durdu, kitapçığı elle seçip kaydedin` });
      setLiveOpen(false);
      return;
    }
    if (serial && clean) {
      try {
        const r = await api.post(`/exams/${data.exam_id}/results`, { student_id: data.student.id, answers: data.answers || {}, source: "omr", booklet: isDeneme ? data.booklet : null });
        beepSuccess();
        setLastFeedback({ ok: true, text: `KAYDEDİLDİ ✓ ${name} · ${marked} işaret · Net ${r.data.total_net} · Puan ${r.data.score} → sıradaki form` });
        setReadTick((t) => t + 1);
        setAnswers({}); setUncertain([]); setStudentId(""); setScannedResult(null);
        setActiveFormId(""); setActiveStudent(null);
        return;
      } catch (e) { beepError(); setLastFeedback({ ok: false, text: `Kaydedilemedi: ${apiError(e)}` }); return; }
    }
    if (clean) beepSuccess(); else beepWarn();
    setLastFeedback(null);
    setLiveOpen(false);
  };

  // Ortak: fotoğraf (dataUrl) → /optical/auto-read → önizleme paneli. formId verilirse QR aranmaz. Başarıysa veriyi döner.
  const submitFrame = async (getDataUrl, formId = "") => {
    setAutoLoading(true);
    setFrameOk(false);
    try {
      const dataUrl = await getDataUrl();
      const { data } = await api.post("/optical/auto-read", { data: formId || "", image_base64: dataUrl });
      setExamId(data.exam_id);
      setStudentId(data.student.id);
      setAnswers(data.answers || {});
      setUncertain(data.uncertain || []);
      setBooklet(data.booklet || "");
      setAnswerKey(data.answer_key || {});
      setScannedResult({ ...data, student: data.student, exam_name: data.exam_name, preview: true });
      setFrameOk(true);
      const unc = (data.uncertain || []).length;
      const marked = data.marked_count ?? Object.keys(data.answers || {}).length;
      if (marked === 0) toast.warning(`QR Okundu ✓ ${data.student.first_name} ${data.student.last_name} · Formda hiç işaret yok (boş form)`, { duration: 6000 });
      else if (unc === 0) toast.success(`QR Okundu ✓ · Optik HATASIZ okundu ✓ ${data.student.first_name} ${data.student.last_name} · ${marked} işaret · Net ${data.total_net} · Puan ${data.score}`, { duration: 6000 });
      else toast.warning(`QR Okundu ✓ · ${unc} soruda belirsiz işaret — kontrol edin. Net ${data.total_net} · Puan ${data.score}`, { duration: 6000 });
      if (data.exam_type === "deneme" && !data.booklet) toast.warning("Kitapçık türü optikten okunamadı — kaydetmeden önce A/B/C/D seçin.", { duration: 7000 });
      return data;
    } catch (e) {
      toast.error(apiError(e, "Okunamadı. Formun 4 köşesi, QR kod ve baloncuklar net ve düz görünecek şekilde, gölgesiz tekrar çekin."));
    } finally { setAutoLoading(false); }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      try { scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
  };

  const resolveQr = async (raw) => {
    try {
      const { data } = await api.post("/optical/resolve-qr", { data: raw });
      if (data.exam_id) setExamId(data.exam_id);
      if (data.student?.id) setStudentId(data.student.id);
      const st = data.student;
      setActiveFormId(data.form_id || "");
      setActiveStudent(st || null);
      setUncertain([]);
      if (data.result) {
        // Kayıtlı cevaplar var: ızgarayı doldur ve anlık sonucu göster
        setAnswers(data.result.answers || {});
        setScannedResult({ ...data.result, student: st, exam_name: data.exam_name, answer_key_filled: data.answer_key_filled });
        toast.success(`Sonuç hazır: ${st?.first_name} ${st?.last_name} · Net ${data.result.total_net} · Puan ${data.result.score}`);
      } else {
        setAnswers({});
        setScannedResult(null);
        if (data.answer_key_filled) toast.success(`Öğrenci bulundu: ${st?.first_name} ${st?.last_name} (${st?.grade}-${st?.branch}). Cevapları girin veya AI ile okuyun.`);
        else toast.warning(`Öğrenci bulundu: ${st?.first_name} ${st?.last_name}. UYARI: Bu sınavın cevap anahtarı eksik, sonuç hesaplanamaz!`);
      }
    } catch (e) { toast.error(apiError(e, "QR çözümlenemedi")); }
  };

  const [scanOk, setScanOk] = useState(false);
  const [frameOk, setFrameOk] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // kamera tarayıcıyı başlat
  useEffect(() => {
    if (!scanOpen) return;
    setScanOk(false);
    const t = setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 15, qrbox: (vw, vh) => { const s = Math.floor(Math.min(vw, vh) * 0.8); return { width: s, height: s }; }, videoConstraints: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } } },
          (decoded) => {
            setScanOk(true);
            if (navigator.vibrate) navigator.vibrate(120);
            toast.success("QR Okundu ✓");
            setTimeout(() => { stopScanner(); setScanOpen(false); resolveQr(decoded); }, 650);
          },
          () => {}
        );
      } catch (e) {
        toast.error("Kamera açılamadı. Kamera iznini kontrol edin veya form ID'yi elle yapıştırın.");
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [scanOpen]);

  const handleScanOpen = (v) => { setScanOpen(v); if (!v) stopScanner(); };

  useEffect(() => { api.get("/exams").then((r) => { setExams(r.data); if (!examId && r.data[0]) setExamId(r.data[0].id); }); api.get("/students").then((r) => setStudents(r.data)); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (examId) api.get(`/exams/${examId}`).then((r) => setExam(r.data)); }, [examId]);

  // Öğrenci seçilince o öğrencinin sınıf seviyesine özel soru dağılımını getir
  useEffect(() => {
    if (!examId) return;
    const st = students.find((s) => s.id === studentId);
    const params = st?.grade ? { grade: st.grade } : {};
    api.get(`/exams/${examId}/grade-config`, { params }).then((r) => {
      setExam((prev) => prev ? { ...prev, subjects: r.data.subjects, options: r.data.options, total_questions: r.data.total_questions, penalty_ratio: r.data.penalty_ratio } : prev);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [studentId, examId, students]);

  // Deneme'de kitapçık elle değiştirilirse doğru/yanlış renklendirmesi için o kitapçığın anahtarını getir
  useEffect(() => {
    const isDeneme = (scannedResult?.exam_type || exam?.exam_type) === "deneme";
    if (!examId || !isDeneme || !booklet) return;
    const st = students.find((s) => s.id === studentId);
    api.get(`/exams/${examId}/answer-key`, { params: { ...(st?.grade ? { grade: st.grade } : {}), booklet } })
      .then((r) => setAnswerKey(r.data.answer_key || {})).catch(() => {});
    // eslint-disable-next-line
  }, [booklet, studentId, examId]);

  const ranges = useMemo(() => {
    if (!exam) return [];
    let run = 0;
    return [...exam.subjects].sort((a, b) => a.order - b.order).map((s) => { const r = { ...s, start: run + 1, end: run + s.question_count }; run += s.question_count; return r; });
  }, [exam]);
  const options = exam?.options || ["A", "B", "C", "D"];

  const setAns = (n, v) => setAnswers((a) => { const na = { ...a }; if (na[String(n)] === v) delete na[String(n)]; else na[String(n)] = v; return na; });

  const onImage = async (ev) => {
    const f = ev.target.files?.[0]; if (!f || !examId) return;
    setAiLoading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/exams/${examId}/optical-read`, { image_base64: String(reader.result) });
        const clean = {};
        Object.entries(data.answers || {}).forEach(([k, v]) => { if (v) clean[k] = v; });
        setAnswers(clean);
        setUncertain(data.uncertain || []);
        if (data.booklet) setBooklet(data.booklet);
        toast.success(`AI okuma tamamlandı. ${data.uncertain?.length || 0} soru kontrol gerektiriyor.`);
      } catch (e) { toast.error(apiError(e, "AI okuma başarısız")); }
      finally { setAiLoading(false); }
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!studentId) { toast.error("Öğrenci seçin"); return; }
    const isDeneme = (scannedResult?.exam_type || exam?.exam_type) === "deneme";
    if (isDeneme && !booklet) { toast.error("Deneme sınavı için kitapçık türünü (A/B/C/D) seçin"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/exams/${examId}/results`, { student_id: studentId, answers, source: uncertain.length ? "ai" : "manual", booklet: isDeneme ? booklet : null });
      setReadTick((t) => t + 1);
      setScannedResult({ ...data, student: students.find((s) => s.id === studentId), exam_name: exam?.name, answer_key_filled: true });
      toast.success(`Sonuç: Net ${data.total_net} · Puan ${data.score}`);
      setAnswers({}); setUncertain([]); setStudentId(""); setBooklet(""); setAnswerKey({});
      setActiveFormId(""); setActiveStudent(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const filledCount = Object.keys(answers).length;

  return (
    <div>
      <PageHeader title="Optik Okuma" desc="Optik formu AI ile okuyun veya cevapları manuel girin, sistem otomatik puanlasın." />

      <Card className="p-5 bg-card border-border mb-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Sınav</Label>
            <Select value={examId} onValueChange={setExamId}>
              <SelectTrigger data-testid="read-exam"><SelectValue placeholder="Sınav seçin" /></SelectTrigger>
              <SelectContent className="bg-card border-border">{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} {e.answer_key_filled ? "" : "(anahtar eksik)"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Öğrenci</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="read-student"><SelectValue placeholder="Öğrenci seçin" /></SelectTrigger>
              <SelectContent className="bg-card border-border max-h-72">{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.grade}-{s.branch} · {s.number} · {s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className={`rounded-xl border p-4 transition-colors ${activeFormId ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-border"}`} data-testid="step-qr">
            <div className="flex items-center gap-2 mb-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</span><span className="font-semibold">QR Oku</span></div>
            {activeStudent ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300 font-semibold mb-3" data-testid="step-active-student"><CheckCircle2 className="h-4 w-4" />{activeStudent.first_name} {activeStudent.last_name} · {activeStudent.grade}-{activeStudent.branch} hazır</div>
            ) : (
              <p className="text-xs text-muted-foreground mb-3">Önce formdaki QR kodu okutun; öğrenci otomatik belirlenir.</p>
            )}
            <Button onClick={() => setScanOpen(true)} data-testid="qr-scan-open" variant={activeFormId ? "outline" : "default"}><Camera className="h-4 w-4 mr-2" />{activeFormId ? "Yeni QR Oku" : "QR Oku"}</Button>
          </div>
          <div className={`rounded-xl border p-4 transition-colors ${activeFormId ? "border-border" : "border-dashed border-border opacity-60"}`} data-testid="step-optic">
            <div className="flex items-center gap-2 mb-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</span><span className="font-semibold">Optiği Oku</span></div>
            <p className="text-xs text-muted-foreground mb-3">{activeFormId ? "Şimdi optik formu okutun — bu adımda QR aranmaz." : "Önce 1. adımda QR okutun."}</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setLiveOpen(true)} disabled={!activeFormId || autoLoading} data-testid="live-camera-open"><ScanLine className="h-4 w-4 mr-2" />Canlı Kamera</Button>
              <Button variant="outline" onClick={() => autoFileRef.current?.click()} disabled={!activeFormId || autoLoading} data-testid="auto-frame-open">{autoLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Aperture className="h-4 w-4 mr-2" />}Fotoğraf</Button>
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => setGuideOpen(true)} data-testid="optical-guide-btn"><HelpCircle className="h-4 w-4 mr-1 text-primary" />Nasıl Çekmeliyim?</Button>
          <span className="text-xs text-muted-foreground">Önce QR, sonra optik: QR ile öğrenci belirlenir, ardından optik okunur — optikte QR görünmesi gerekmez.</span>
          <input ref={autoFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onAutoFrame} data-testid="auto-frame-input" />
          <div id="qr-file-reader" className="hidden" />
        </div>
      </Card>

      <LiveCamera open={liveOpen} onOpenChange={(v) => { setLiveOpen(v); if (!v) setLastFeedback(null); }} onCapture={onLiveCapture} busy={autoLoading} serial={serial} onSerialChange={setSerial} lastFeedback={lastFeedback} formId={activeFormId} hideSerial />
      <ReadStatus examId={examId} refreshKey={readTick} />
      <Dialog open={scanOpen} onOpenChange={handleScanOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle>QR Kod Tara</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div id="qr-reader" className={`w-full rounded-lg overflow-hidden min-h-[320px] relative transition-all duration-200 ${scanOk ? "ring-4 ring-emerald-500 border-2 border-emerald-500" : "border border-border"}`} data-testid="qr-reader" />
            {scanOk && <div className="flex items-center justify-center gap-2 rounded-lg bg-emerald-500 text-white font-bold text-base py-3 animate-pulse" data-testid="qr-scan-ok"><CheckCircle2 className="h-6 w-6" />QR OKUNDU ✓ — öğrenci getiriliyor…</div>}
            <div className="flex gap-2">
              <Input value={manualQr} onChange={(e) => setManualQr(e.target.value)} placeholder="veya form ID yapıştırın" className="font-mono text-xs" data-testid="qr-manual-input" />
              <Button variant="outline" onClick={() => { if (manualQr.trim()) { handleScanOpen(false); resolveQr(manualQr.trim()); } }} data-testid="qr-manual-btn">Bul</Button>
            </div>
            <p className="text-xs text-muted-foreground">Kamera erişimi için tarayıcı izni gerekir (HTTPS üzerinde çalışır). İzin verilmezse optik formun QR payload'ını veya form ID'sini yukarıya yapıştırabilirsiniz.</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-card border-border max-w-5xl p-2">
          <DialogHeader><DialogTitle className="px-2">Okuma Önizlemesi</DialogTitle></DialogHeader>
          {scannedResult?.preview_image && <img src={scannedResult.preview_image} alt="Okuma önizlemesi büyük" className="w-full max-h-[80vh] object-contain rounded" data-testid="omr-preview-large" />}
        </DialogContent>
      </Dialog>

      {/* Görsel Çekim Rehberi Dialog */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle>Optik Form Nasıl Çekilmeli?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <img src="/kamera_rehberi.png" alt="Optik Çekim Rehberi" className="w-full rounded-lg border border-border" />
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              <div className="p-2 rounded bg-muted/40 border border-border">
                <b>1. 4 Köşe Karesi</b>
                <p className="text-muted-foreground mt-0.5">Formun 4 köşesindeki siyah kareler mutlaka kadrajda olmalı.</p>
              </div>
              <div className="p-2 rounded bg-muted/40 border border-border">
                <b>2. Sağ Üstteki QR Kod</b>
                <p className="text-muted-foreground mt-0.5">Öğrenciyi otomatik tanımak için QR kod net görünmeli.</p>
              </div>
              <div className="p-2 rounded bg-muted/40 border border-border">
                <b>3. Baloncuklar</b>
                <p className="text-muted-foreground mt-0.5">Kurşun kalem işaretleri belirgin, gölgesiz olmalı.</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR okutunca anlık sonuç paneli */}
      {scannedResult && (
        <Card className={`p-5 bg-card border-border mb-6 ${scannedResult.preview ? (frameOk ? "ring-4 ring-emerald-500" : "ring-2 ring-amber-400") : ""}`} data-testid="qr-result-panel">
          {scannedResult.preview && frameOk && (
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 rounded-lg bg-emerald-500 text-white font-bold text-sm px-3 py-2" data-testid="frame-qr-ok"><CheckCircle2 className="h-5 w-5" />QR OKUNDU ✓ · {scannedResult.student?.first_name} {scannedResult.student?.last_name} · {scannedResult.student?.grade}-{scannedResult.student?.branch}</div>
              {(scannedResult.uncertain || []).length === 0 && (scannedResult.marked_count ?? Object.keys(scannedResult.answers || {}).length) > 0 ? (
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-500 text-emerald-800 dark:text-emerald-300 font-semibold text-sm px-3 py-2" data-testid="frame-read-ok"><CheckCircle2 className="h-5 w-5" />Optik okuma HATASIZ ✓ · {scannedResult.marked_count ?? Object.keys(scannedResult.answers || {}).length} işaretli · {scannedResult.blank_count ?? 0} boş · belirsiz soru yok</div>
              ) : (scannedResult.marked_count ?? Object.keys(scannedResult.answers || {}).length) === 0 ? (
                <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-500 text-amber-800 dark:text-amber-300 font-semibold text-sm px-3 py-2" data-testid="frame-read-blank"><AlertTriangle className="h-5 w-5" />Formda hiç işaret bulunamadı (BOŞ FORM). Tüm sorular boş sayılacak — kaydetmeden önce kontrol edin.</div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-500 text-amber-800 dark:text-amber-300 font-semibold text-sm px-3 py-2" data-testid="frame-read-warn"><AlertTriangle className="h-5 w-5" />DİKKAT: {(scannedResult.uncertain || []).length} soruda birden fazla/belirsiz işaret var (Soru {(scannedResult.uncertain || []).join(", ")}). Önizlemeden kontrol edip düzeltin.</div>
              )}
            </div>
          )}
          {scannedResult.preview && scannedResult.preview_image && (
            <div className="mb-4" data-testid="omr-preview-wrap">
              <div className="flex flex-wrap items-center gap-3 text-xs mb-2">
                <span className="font-bold text-sm">Okunan Optik — Renkli Kontrol</span>
                <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full border-2 border-green-600" />Doğru okundu</span>
                <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full border-2 border-red-600" />Yanlış (okunan şık)</span>
                <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full border-2 border-sky-600" />Doğru şık (anahtar)</span>
                <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-full border-2 border-orange-500" />Belirsiz / çift</span>
              </div>
              <img src={scannedResult.preview_image} alt="Okunan optik renkli" onClick={() => setPreviewOpen(true)} className="w-full rounded-lg border-2 border-primary/40 cursor-zoom-in" data-testid="omr-preview-img" />
              <p className="text-[11px] text-muted-foreground mt-1">Okunan optiği burada renkli kontrol edin; büyütmek için tıklayın. Hatalı okunan soruyu aşağıdaki ızgaradan düzeltip "Onayla & Kaydet" deyin.</p>
            </div>
          )}
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="font-bold text-lg tracking-tight">{scannedResult.student?.first_name} {scannedResult.student?.last_name}{scannedResult.preview && <Badge className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Önizleme · kaydedilmedi</Badge>}</h3>
              <p className="text-sm text-muted-foreground">{scannedResult.exam_name} · {scannedResult.student?.grade}-{scannedResult.student?.branch} · No {scannedResult.student?.number}</p>
              {scannedResult.merge && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-sky-50 dark:bg-sky-950/40 border border-sky-400 text-sky-800 dark:text-sky-300 text-xs font-semibold px-2 py-1" data-testid="supp-merge-note">
                  <Layers className="h-3.5 w-3.5" />İlave bölüm okuması — yeni dersler mevcut sonuca eklendi (toplam 6 ders)
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center"><div className="text-[10px] uppercase font-semibold text-muted-foreground">Net</div><div className="text-2xl font-extrabold font-mono">{scannedResult.total_net}</div></div>
              <div className="text-center"><div className="text-[10px] uppercase font-semibold text-muted-foreground">Puan</div><div className="text-2xl font-extrabold font-mono text-primary">{scannedResult.score}</div></div>
              {scannedResult.preview ? (
                <>
                  <Button size="sm" onClick={save} disabled={saving || (scannedResult.exam_type === "deneme" && !booklet)} data-testid="qr-result-confirm">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Onayla & Kaydet</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setScannedResult(null); setAnswers({}); setStudentId(""); setUncertain([]); setActiveFormId(""); setActiveStudent(null); }} data-testid="qr-result-cancel">Vazgeç</Button>
                </>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setScannedResult(null)} data-testid="qr-result-close">Kapat</Button>
              )}
            </div>
          </div>
          {scannedResult.exam_type === "deneme" && (
            <div className="flex items-center gap-2 mb-4 flex-wrap bg-muted/40 rounded-lg px-3 py-2" data-testid="reader-booklet-row">
              <span className="text-sm font-semibold">Kitapçık Türü:</span>
              {scannedResult.booklet && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-white text-sm font-bold px-3 py-1" data-testid="booklet-auto-detected">
                  <CheckCircle2 className="h-4 w-4" />Otomatik okundu: {scannedResult.booklet}
                </span>
              )}
              {(scannedResult.booklets || ["A", "B", "C", "D"]).map((b) => (
                <button key={b} onClick={() => setBooklet(b)} data-testid={`reader-booklet-${b}`}
                  className={`h-9 w-9 rounded-full text-sm font-bold border transition-colors ${booklet === b ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60 text-foreground/70"}`}>{b}</button>
              ))}
              {scannedResult.booklet
                ? <span className="text-[11px] text-muted-foreground ml-1">Yanlışsa üstteki butonlardan değiştirebilirsiniz.</span>
                : <span className="text-xs text-amber-600 font-semibold ml-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Kitapçık okunamadı — lütfen elle seçin</span>}
            </div>
          )}
          <div className="overflow-x-auto ktt-scroll">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr><th className="text-left p-2">Ders</th><th className="text-center p-2">Soru</th><th className="text-center p-2">Doğru</th><th className="text-center p-2">Yanlış</th><th className="text-center p-2">Boş</th><th className="text-center p-2">Net</th></tr>
              </thead>
              <tbody>
                {(scannedResult.per_subject || []).map((ps) => (
                  <tr key={ps.key} className="border-t border-border">
                    <td className="p-2 font-medium">{ps.name}</td>
                    <td className="p-2 text-center font-mono">{ps.total}</td>
                    <td className="p-2 text-center font-mono text-emerald-600 font-semibold">{ps.correct}</td>
                    <td className="p-2 text-center font-mono text-red-600 font-semibold">{ps.wrong}</td>
                    <td className="p-2 text-center font-mono text-muted-foreground">{ps.blank}</td>
                    <td className="p-2 text-center font-mono font-bold">{ps.net}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-border font-bold bg-muted/30">
                  <td className="p-2">TOPLAM</td>
                  <td className="p-2 text-center font-mono">{scannedResult.total_questions}</td>
                  <td className="p-2 text-center font-mono text-emerald-600">{scannedResult.total_correct}</td>
                  <td className="p-2 text-center font-mono text-red-600">{scannedResult.total_wrong}</td>
                  <td className="p-2 text-center font-mono text-muted-foreground">{scannedResult.total_blank}</td>
                  <td className="p-2 text-center font-mono">{scannedResult.total_net}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Net = Doğru − (Yanlış ÷ {exam?.penalty_ratio || 4}). {scannedResult.preview ? "Fotoğraftan okunan cevaplar aşağıdaki ızgaraya yazıldı; hatalı okunanları düzeltip \"Onayla & Kaydet\" diyebilirsiniz." : "Cevap ızgarası kayıtla dolduruldu; düzeltip tekrar \"Puanla & Kaydet\" diyebilirsiniz."}{scannedResult.uncertain?.length ? ` ${scannedResult.uncertain.length} soru kontrol gerektiriyor.` : ""}</p>
        </Card>
      )}
      {!exam ? <EmptyState icon={QrCode} title="Sınav seçin" desc="Okumaya başlamak için yukarıdan bir sınav seçin." /> : (
        <Tabs defaultValue="manual">
          <TabsList><TabsTrigger value="manual" data-testid="tab-manual"><Keyboard className="h-4 w-4 mr-2" />Manuel Giriş</TabsTrigger><TabsTrigger value="ai" data-testid="tab-ai"><Sparkles className="h-4 w-4 mr-2" />AI Okuma</TabsTrigger></TabsList>

          <TabsContent value="ai" className="mt-4">
            <Card className="p-6 bg-card border-border">
              <label className="flex flex-col items-center justify-center gap-3 py-10 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/60" data-testid="ai-upload">
                {aiLoading ? <Loader2 className="h-10 w-10 text-primary animate-spin" /> : <Upload className="h-10 w-10 text-muted-foreground" />}
                <div className="text-sm font-medium">{aiLoading ? "Görsel analiz ediliyor..." : "Optik form fotoğrafı yükleyin"}</div>
                <div className="text-xs text-muted-foreground">JPG / PNG · AI işaretli cevapları tespit eder</div>
                <input type="file" accept="image/*" className="hidden" onChange={onImage} disabled={aiLoading} />
              </label>
              <p className="text-xs text-muted-foreground mt-3">Not: AI okuma için Ayarlar'da bir OpenAI/Gemini anahtarı veya Emergent anahtarı gereklidir. Okuma sonrası aşağıdaki ızgaradan düzeltme yapabilirsiniz.</p>
            </Card>
          </TabsContent>
          <TabsContent value="manual" className="mt-4">
            <p className="text-sm text-muted-foreground mb-4">Her soru için işaretlenen şıkka tıklayın. Boş bırakılanlar "boş" sayılır.</p>
          </TabsContent>

          {/* Shared answer grid */}
          {exam?.exam_type === "deneme" && (
            <div className="flex items-center gap-2 mt-4 flex-wrap bg-muted/40 rounded-lg px-3 py-2" data-testid="manual-booklet-row">
              <span className="text-sm font-semibold">Kitapçık Türü:</span>
              {(exam.booklets || ["A", "B", "C", "D"]).map((b) => (
                <button key={b} onClick={() => setBooklet(b)} data-testid={`manual-booklet-${b}`}
                  className={`h-9 w-9 rounded-full text-sm font-bold border transition-colors ${booklet === b ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60 text-foreground/70"}`}>{b}</button>
              ))}
              <span className="text-[11px] text-muted-foreground ml-1">Manuel/AI girişte öğrencinin kitapçığını seçin.</span>
            </div>
          )}
          <div className="flex items-center justify-between my-4">
            <div className="text-sm"><span className="font-bold">{filledCount}</span> / {exam.total_questions} işaretlendi {uncertain.length > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"><AlertTriangle className="h-3 w-3 mr-1" />{uncertain.length} kontrol gerekli</Badge>}</div>
            <Button onClick={save} disabled={saving || !studentId || (exam?.exam_type === "deneme" && !booklet)} data-testid="read-save">{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Puanla & Kaydet</Button>
          </div>

          {Object.keys(answerKey).length > 0 && (
            <div className="flex flex-wrap items-center gap-3 text-xs mb-3 bg-muted/40 rounded-lg px-3 py-2" data-testid="grid-legend">
              <span className="font-semibold">Izgarada doğru/yanlış:</span>
              <span className="flex items-center gap-1"><span className="inline-block h-3.5 w-3.5 rounded-full bg-emerald-500" />Doğru işaret</span>
              <span className="flex items-center gap-1"><span className="inline-block h-3.5 w-3.5 rounded-full bg-red-500" />Yanlış işaret</span>
              <span className="flex items-center gap-1"><span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-sky-500" />Doğru cevap (anahtar)</span>
              <span className="text-muted-foreground">· Hatalıysa doğru şıkka tıklayıp düzeltin, "Puanla & Kaydet" deyin</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ranges.map((s) => {
              const c = subjectColor(s.key);
              return (
                <Card key={s.key} className="p-4 bg-card border-border">
                  <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${c.badge}`}>{s.name}</span>
                  <div className="space-y-1.5 mt-3">
                    {Array.from({ length: s.question_count }, (_, i) => s.start + i).map((n) => {
                      const unc = uncertain.includes(n);
                      return (
                        <div key={n} className={`flex items-center gap-2 rounded ${unc ? "bg-amber-50 dark:bg-amber-950/30 ring-1 ring-amber-400" : ""}`}>
                          <span className="w-7 text-right font-mono text-xs font-semibold text-muted-foreground">{n}</span>
                          <div className="flex gap-1.5">
                            {options.map((o) => {
                              const sel = answers[String(n)] === o;
                              const correct = answerKey[String(n)];
                              const hasKey = !!correct;
                              let cls = "border-border hover:border-primary/60 text-foreground/70";
                              if (sel && hasKey) cls = (o === correct) ? "bg-emerald-500 text-white border-emerald-500" : "bg-red-500 text-white border-red-500";
                              else if (sel) cls = "bg-primary text-primary-foreground border-primary";
                              else if (hasKey && o === correct) cls = "border-2 border-sky-500 text-sky-600 font-bold";
                              return <button key={o} onClick={() => setAns(n, o)} data-testid={`read-${n}-${o}`} className={`h-7 w-7 rounded-full border text-xs font-semibold transition-colors ${cls}`}>{o}</button>;
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        </Tabs>
      )}
    </div>
  );
}
